import { Component, computed, inject, input, linkedSignal, untracked } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { buildMultiDeviceGridColumns } from '@vectronic/util';
import {
  RuleGroup,
  ScheduleCompareCollar,
  ScheduleCompareService,
  ScheduleTypeGroup,
} from '../../services/schedule-compare.service';
import { SequenceGroup } from '../../services/schedule-format.service';

export type { ScheduleCompareCollar };

@Component({
  selector: 'rc-schedule-compare',
  templateUrl: './schedule-compare.component.html',
  styleUrls: ['./schedule-compare.component.scss'],
  imports: [MatIconButton, MatIcon],
})
export class ScheduleCompareComponent {
  private readonly scheduleCompare = inject(ScheduleCompareService);

  collars = input<ScheduleCompareCollar[]>([]);
  showMode = input<'differences' | 'all'>('all');
  shouldInitiallyExpanded = input<boolean>();
  columnWidth = input<string>('180px');
  columnWidthByDeviceCount = input<Partial<Record<number, string>>>();
  hideHeaders = input<boolean>(false);
  hideScroll = input<boolean>(false);

  gridColumns = computed(() => {
    const deviceCount = this.collars().length;
    return buildMultiDeviceGridColumns(
      deviceCount,
      this.columnWidth(),
      this.columnWidthByDeviceCount(),
    );
  });
  scheduleTypeGroups = computed<ScheduleTypeGroup[]>(() =>
    this.scheduleCompare.buildScheduleTypeGroups(this.collars()),
  );

  expandedCategories = linkedSignal<ScheduleTypeGroup[], Set<string>>({
    source: this.scheduleTypeGroups,
    computation: (groups, previous) => {
      const alreadyHasSelections = previous !== undefined && previous.value.size > 0;
      if (alreadyHasSelections) {
        return previous.value;
      }
      if (untracked(this.shouldInitiallyExpanded)) {
        return new Set(groups.map((g) => g.scheduleType));
      }
      return groups.length > 0 ? new Set([groups[0].scheduleType]) : new Set();
    },
  });

  expandedRules = linkedSignal<ScheduleTypeGroup[], Set<string>>({
    source: this.scheduleTypeGroups,
    computation: (groups, previous) => {
      const alreadyHasSelections = previous !== undefined && previous.value.size > 0;
      if (alreadyHasSelections) {
        return previous.value;
      }
      if (untracked(this.shouldInitiallyExpanded)) {
        return new Set(groups.flatMap((g) => g.rules.map((_, i) => `${g.scheduleType}::${i}`)));
      }
      const firstGroup = groups[0];
      if (firstGroup?.rules.length > 0) {
        return new Set([`${firstGroup.scheduleType}::0`]);
      }
      return new Set();
    },
  });

  isCategoryExpanded(scheduleType: string): boolean {
    return this.expandedCategories().has(scheduleType);
  }

  toggleCategory(scheduleType: string): void {
    this.expandedCategories.update((set) => {
      const next = new Set(set);
      if (next.has(scheduleType)) {
        next.delete(scheduleType);
      } else {
        next.add(scheduleType);
      }
      return next;
    });
  }

  isRuleExpanded(ruleKey: string): boolean {
    return this.expandedRules().has(ruleKey);
  }

  toggleRule(ruleKey: string): void {
    this.expandedRules.update((set) => {
      const next = new Set(set);
      if (next.has(ruleKey)) {
        next.delete(ruleKey);
      } else {
        next.add(ruleKey);
      }
      return next;
    });
  }

  ruleKey(scheduleType: string, ruleIndex: number): string {
    return `${scheduleType}::${ruleIndex}`;
  }

  ruleHasDiff(rule: RuleGroup): boolean {
    return (
      rule.fields?.some((f) => f.hasDiff) ||
      rule.sequences?.some((seq) => this.sequenceHasDiff(seq))
    );
  }

  sequenceHasDiff(seq: SequenceGroup): boolean {
    return seq.fields?.some((f) => f.hasDiff) ?? false;
  }

  groupHasDiff(group: ScheduleTypeGroup): boolean {
    return group.rules?.some((rule) => this.ruleHasDiff(rule));
  }

  trackByScheduleType(_: number, group: ScheduleTypeGroup): string {
    return group.scheduleType;
  }

  trackByLabel(_: number, item: { label: string }): string {
    return item.label;
  }
}
